<template>
  <div>
    <McButton
      v-bind="buttonProps"
      variant="secondary"
      :class="{
        isSearchWithFilterStyle: isSearchWithFilter,
        'mc-btn__badge': hasDefaultFilter || hasActiveFilter,
      }"
      :text="title"
      icon="icon-outline_filter"
      :disabled="isDisabled"
      @click="showModal = !showModal"
    />

    <b-modal
      v-model="showModal"
      size="md"
      :title="title"
      modal-class="modal-filter"
      hide-header-close
      scrollable
    >
      <div class="card-body p-2">
        <div v-if="hasSaveFilter" class="mc-modal__filter-item">
          <FormulateInput
            id="savedFilter"
            v-model="selectedFilter"
            name="savedFilter"
            type="select-resourceful-v2"
            trackBy="label"
            placeholder="Pilih Filter Tersimpan"
            label="Filter Tersimpan"
            :optionsGetter="resourcefulQuery.userPreferences"
            :taggable="false"
            :multiple="false"
            :hasDelete="true"
            :optionLabelFn="({ item }) => item.label"
            :fullObject="filterObject"
            @update:fullObject="($event) => (filterObject = $event)"
            @input="setFilter"
            @delete="handleDeleteFilter"
          />
        </div>
        <FormulateForm ref="formRef" v-model="formValues">
          <slot
            name="content"
            :formValues="formValues"
            :updateFormValues="updateFormValues"
            :fullObjects="fullObjects"
            :updateFullObjects="updateFullObjects"
          />
        </FormulateForm>
      </div>
      <template #modal-header>
        <span class="__title"> {{ title }} </span>
        <span v-if="hasResetFilters" class="__reset" @click="handleReset">
          {{ $t('global.resetAsset') }}
        </span>
      </template>
      <template #modal-footer="{ cancel }">
        <div class="d-flex justify-content-between w-100">
          <div>
            <McButton
              v-if="hasSaveFilter"
              v-b-modal.modal-save-filter
              variant="secondary"
              text="Simpan Filter"
              icon="icon-outline_bookmark"
            />
          </div>
          <div>
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.cancel')"
              @click="cancel"
            />
            <McButton
              variant="primary"
              :text="$t('global.action.apply')"
              :disabled="$refs.formRef?.hasErrors"
              @click="handleAccept"
            />
          </div>
        </div>
      </template>
    </b-modal>
    <b-modal
      id="modal-save-filter"
      :title="$t('controlTower.filter.saveFilter')"
      size="md"
      centered
      no-close-on-esc
    >
      <div class="card-body p-2">
        <span>{{ $t('controlTower.filter.doYouWantToSave') }}</span>
      </div>
      <template #modal-footer>
        <McButton
          variant="primary"
          :text="$t('controlTower.filter.saveNew')"
          class="me-2"
          @click="openModalFilter('create')"
        />
        <McButton
          variant="secondary"
          :text="$t('controlTower.filter.changePresets')"
          @click="openModalFilter('update')"
        />
      </template>
    </b-modal>
    <b-modal
      id="modal-create-filter"
      :title="$t('controlTower.filter.saveNewFilter')"
      size="md"
      centered
      hide-header-close
      no-close-on-esc
    >
      <div class="card-body p-2">
        <FormulateInput
          id="filterName"
          v-model="filterName"
          name="filterName"
          :label="$t('controlTower.filter.filterName')"
          type="text"
          :placeholder="$t('controlTower.filter.fillFilterName')"
          validation-behavior="live"
          error-behavior="blur"
          validation="bail|max:32,length|required:trim"
          :help="true"
        />
      </div>
      <template #modal-footer="{ cancel }">
        <McButton
          variant="secondary"
          :text="$t('global.action.close')"
          class="me-2"
          @click="cancel"
        />
        <McButton
          variant="primary"
          :text="$t('controlTower.filter.saveNew')"
          :disabled="!filterName || filterName.length > 32"
          @click="handleSaveFilter"
        />
      </template>
    </b-modal>

    <div v-if="showDeleteModal" id="modal-delete-filter" class="mc-modal--custom">
      <div class="mc-modal--custom-wrapper">
        <h4 class="mc-modal--custom-title">
          {{ $t('controlTower.filter.deleteTitle') }} ?
          <i class="mc-fa icon-outline_close" @click="showDeleteModal = false"></i>
        </h4>
        <div class="mc-modal--custom-content">
          {{ $t('controlTower.filter.deleteContent') }} <strong>{{ selectedFilterLabel }}</strong> ?
        </div>
        <div class="mc-modal__filter-footer">
          <div class="d-flex flex-row-reverse text-end">
            <div>
              <McButton
                variant="secondary"
                class="me-2"
                :text="$t('global.action.cancel')"
                @click="showDeleteModal = false"
              />
              <McButton
                variant="primary"
                :text="$t('controlTower.filter.deleteConfirm')"
                @click="handleDeleteFilterConfirm(selectedFilterLabel)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { Helper } from '@/mixin/helper'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  props: {
    title: {
      type: String,
      default: 'Filter',
    },
    activeFilters: {
      type: Object,
      required: true,
    },
    parseFilterDataMappings: {
      type: Object,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    hasResetFilters: {
      type: Boolean,
      default: true,
    },
    isSearchWithFilter: {
      type: Boolean,
      default: false,
    },
    hasCustomEvent: {
      type: Boolean,
      default: false,
    },
    hasSaveFilter: {
      type: Boolean,
      default: false,
    },
    presetFilters: {
      type: Array,
      default: () => [],
    },
    presetFullObjects: {
      type: Object,
      default: () => {},
    },
    hasChangeData: {
      type: Boolean,
      default: false,
    },
    hasDefaultFilter: {
      type: Boolean,
      default: false,
    },
    defaultValues: {
      type: Object,
      default: () => ({}),
    },
    buttonProps: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['submit'],
  data() {
    return {
      showModal: false,
      formValues: {},
      oldFormValues: {},
      fullObjects: {},
      oldFullObjects: {},
      isOpenDropdown: false,
      selectedFilter: null,
      filterObject: {},
      filterName: null,
      selectedRows: {},
      selectedFilterLabel: '',
      showDeleteModal: false,
    }
  },
  computed: {
    resourcefulQuery() {
      return {
        userPreferences: (opts) => {
          let result = {}
          let data = this.presetFilters
          if (opts.search) data = data.filter((item) => this.isContains(opts.search, item.label))

          result = {
            metadata: {
              total_page: 1,
              total_count: 5,
              page: 1,
            },
            data: {
              paginated_result: data,
            },
          }

          return result
        },
      }
    },
    hasActiveFilter() {
      // New behavior: if defaultValues provided
      if (this.defaultValues && Object.keys(this.defaultValues).length > 0) {
        if (!this.activeFilters || Object.keys(this.activeFilters).length === 0) {
          return false
        }

        return Object.entries(this.activeFilters).some(([key, value]) => {
          const defaultVal = this.defaultValues[key]

          if (value === null || value === undefined) return false
          if (value === false) return false
          if (value === '') return false

          // Compare arrays by length
          if (Array.isArray(value)) {
            if (value.length === 0) return false
            if (Array.isArray(defaultVal)) return value.length !== defaultVal.length
            return value.length > 0
          }

          // Compare strings/other values directly
          if (defaultVal !== undefined) return value !== defaultVal

          if (value === true) return true

          return false
        })
      }

      // Original behavior
      if (!this.oldFormValues || Object.keys(this.oldFormValues).length === 0) {
        return false
      }
      return Object.entries(this.oldFormValues).some(([_key, value]) => {
        if (Array.isArray(value)) {
          return (
            value.length > 0 &&
            value.some((item) => item !== null && item !== undefined && item !== '')
          )
        }
        return value !== null && value !== undefined && value !== ''
      })
    },
  },
  watch: {
    showModal(newValue) {
      this.$emit('update:showModal', newValue)
      if (!newValue) return

      Object.keys(this.parseFilterDataMappings).forEach((key) => {
        if (!Array.isArray(this.parseFilterDataMappings[key])) {
          this.$set(this.formValues, key, this.activeFilters[this.parseFilterDataMappings[key]])
          return
        }

        const arr = []
        this.parseFilterDataMappings[key].forEach((keyMapping) => {
          arr.push(this.activeFilters[keyMapping])
        })

        this.$set(this.formValues, key, arr)
      })

      this.fullObjects = cloneDeep(this.oldFullObjects)
    },
    presetFullObjects: {
      handler(newValue) {
        if (newValue) {
          this.oldFullObjects = cloneDeep(newValue)
        }
      },
      immediate: false,
    },
  },
  mounted() {
    if (this.presetFullObjects) {
      this.oldFullObjects = cloneDeep(this.presetFullObjects)
    } else {
      setTimeout(() => {
        if (this.presetFullObjects) {
          this.oldFullObjects = cloneDeep(this.presetFullObjects)
        }
      }, 300)
    }
  },
  methods: {
    updateFormValues({ key, value }) {
      this.$set(this.formValues, key, value)
    },
    updateFullObjects({ key, value }) {
      this.$set(this.fullObjects, key, value)
    },
    parseData() {
      const newValueKeys = Object.keys(this.formValues)
      const filters = []

      newValueKeys.forEach((key) => {
        // mapping the filter data to be emit
        if (!Array.isArray(this.parseFilterDataMappings[key])) {
          filters.push({ key: this.parseFilterDataMappings[key], values: this.formValues[key] })
          return
        }

        this.parseFilterDataMappings[key].forEach((keyMapping, index) => {
          filters.push({ key: keyMapping, values: this.formValues[key][index] })
        })
      })
      return filters
    },
    handleAccept() {
      if (!isEqual(this.formValues, this.oldFormValues) || this.hasChangeData) {
        this.$emit('submit', this.parseData())
        this.oldFormValues = cloneDeep(this.formValues)
        this.oldFullObjects = cloneDeep(this.fullObjects)
        this.$emit('getFullObjects', this.fullObjects)
      }
      if (!this.hasCustomEvent || isEqual(this.fullObjects, this.oldFullObjects)) {
        this.showModal = false
      }
    },
    async handleReset() {
      this.formValues = {}
      this.fullObjects = {}
      this.oldFormValues = {}
      this.oldFullObjects = {}
      if (this.hasSaveFilter) {
        this.selectedFilter = null
        this.filterObject = {}
      }
      const emptyFilter = []
      Object.keys(this.parseFilterDataMappings).forEach((key) => {
        if (!Array.isArray(this.parseFilterDataMappings[key])) {
          emptyFilter.push({ key: this.parseFilterDataMappings[key], values: null })
          return
        }

        this.parseFilterDataMappings[key].forEach((keyMapping) => {
          emptyFilter.push({ key: keyMapping, values: null })
        })
      })
      this.$emit('reset')
      this.$emit('submit', emptyFilter)
      this.showModal = false
    },

    handleResetFormOnly() {
      this.formValues = {}
      this.fullObjects = {}
      this.oldFormValues = {}
      this.oldFullObjects = {}
      if (this.hasSaveFilter) {
        this.selectedFilter = null
        this.filterObject = {}
      }
      const emptyFilter = []
      Object.keys(this.parseFilterDataMappings).forEach((key) => {
        if (!Array.isArray(this.parseFilterDataMappings[key])) {
          emptyFilter.push({ key: this.parseFilterDataMappings[key], values: null })
          return
        }

        this.parseFilterDataMappings[key].forEach((keyMapping) => {
          emptyFilter.push({ key: keyMapping, values: null })
        })
      })
      this.$emit('reset')
      this.$emit('submit', emptyFilter)
    },

    openModalFilter(params) {
      if (params == 'create') this.$bvModal.show('modal-create-filter')
      else this.$bvModal.show('modal-edit-filter')
      this.$bvModal.hide('modal-save-filter')
    },
    async setFilter() {
      if (!this.selectedFilter) return
      await this.$nextTick()
      if (!this.filterObject?.value) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      this.formValues = this.filterObject?.value?.activeFilters
      this.$emit('setFilter', this.formValues)
      this.fullObjects = cloneDeep(this.presetFullObjects)
    },
    handleSaveFilter() {
      const input = {
        name: this.filterName,
        value: {
          activeFilters: this.formValues,
        },
      }
      this.$emit('saveFilter', input)

      this.filterName = ''
    },

    handleDeleteFilter(label) {
      this.selectedFilterLabel = label
      this.showDeleteModal = true
    },

    handleDeleteFilterConfirm() {
      this.$emit('deleteFilter', this.selectedFilterLabel)
      this.showDeleteModal = false
      this.handleResetFormOnly()
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.modal-filter) {
  .modal-dialog {
    border-radius: 0px;
    overflow: hidden;
    height: 100%;
    margin: 0;
    margin-left: auto;
  }

  .modal-content {
    height: 100%;
  }

  .modal-content,
  .modal-header,
  .modal-footer {
    border-radius: 0;
  }

  .modal-header {
    color: $white;
    justify-content: space-between;

    .__reset {
      font-size: 13px;
      text-align: right;
      cursor: pointer;
    }
  }
}
.isSearchWithFilterStyle {
  position: relative;
  right: 46px;
  top: 98px;
  z-index: 3;
}

.mc-modal--custom {
  width: 400px;
  box-sizing: border-box;
  background: $white;
  position: fixed;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 999999;
  margin: 0 auto;
  text-align: left;
  display: block;
  border-radius: 5px;
  overflow: hidden;
  &:before {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    content: '';
    background: rgba(0, 0, 0, 0.3);
    z-index: 1;
  }
}

.mc-modal--custom-wrapper {
  position: relative;
  z-index: 2;
  background-color: $white;
}

.mc-modal--custom-title {
  padding: 15px 10px;
  background-color: $mep-primary;
  color: $white;
  font-size: 14px;
  position: relative;

  .mc-fa {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;

    &:before {
      color: $white;
    }
  }
}

.mc-modal--custom-content {
  padding: 10px;
}

.mc-modal__filter-footer {
  margin: 0;
  text-align: right;
  margin-top: 30px;
  padding: 10px;
}

.mc-btn__badge::after {
  position: absolute;
  right: -4px;
  top: -4px;
  width: 12px;
  height: 12px;
  border-radius: 100%;
  background-color: $red;
  content: '';
}
</style>
